import React, { useState } from 'react';
import { Globe } from 'lucide-react';
import { getSourceFaviconUrl, getSourceHostname } from '../../utils/sourceFavicon';

interface SourceCitationProps {
  label: string;
  href: string;
  title?: string;
  linkProps?: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'title'>;
}

/** Web citation marker: the website favicon in a round badge that opens the source page. */
export const SourceCitation: React.FC<SourceCitationProps> = ({
  label,
  href,
  title,
  linkProps,
}) => {
  const [faviconFailed, setFaviconFailed] = useState(false);
  const faviconUrl = getSourceFaviconUrl(href);
  const hostname = getSourceHostname(href);
  const tooltip = [title, hostname].filter(Boolean).join(' - ');

  return (
    <a
      {...linkProps}
      href={href}
      title={tooltip || undefined}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={title ? `Open source ${label}: ${title}` : `Open source ${label}`}
      className="mx-0.5 inline-flex h-5 w-5 translate-y-0.5 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white align-baseline no-underline shadow-sm transition-transform hover:scale-110 hover:border-blue-300 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-sky-400/60"
    >
      {faviconUrl && !faviconFailed ? (
        <img
          src={faviconUrl}
          alt=""
          width={16}
          height={16}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="h-4 w-4 rounded-full object-cover"
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <Globe
          size={12}
          strokeWidth={2}
          className="text-gray-500 dark:text-slate-300"
          aria-hidden="true"
        />
      )}
    </a>
  );
};
